// Which palette a form's stored colour stands for (CON-331).
//
// WHY A VALUE MATCH, AND NOT A "NO COLOUR" FIELD. A form saved without a
// colour does not arrive without one. Until PR 1542 the dashboard wrote its
// `FORM_DEFAULT_COLOR` placeholder into every such form, so those forms carry
// `lightColor: LEGACY_UNBRANDED_FORM_COLOR` in the table, the API returns it,
// and nothing on the wire tells it apart from a colour a nonprofit picked. The
// backends are frozen, so the stored value cannot be backfilled from here.
//
// Expanded as a brand, that placeholder is not neutral: `generateBrandColors`
// pins saturation, so its pressed step and text step come out a clearly blue
// hue, and every donor surface painted it (the checkout's Back link, the
// hosted page's organisation link). The decision (D-con-331-placeholder):
//
//   - exactly this one stored string, byte for byte, means "this organisation
//     has no brand colour", and paints the neutral donor set (`UNBRANDED_THEME`),
//     the same one an empty payload gets. The match is case-sensitive on
//     purpose: the dashboard wrote the constant in upper case, while its colour
//     pickers (`<input type="color">`, and the pre-1542 SketchPicker) report
//     lower case, so an organisation that picked the same slate keeps its brand;
//   - EVERY other input goes to `generateBrandColors` untouched, so a branded
//     organisation's palette is byte-identical — including a low-chroma slate
//     or grey an organisation genuinely chose. Only the one literal is claimed.
//
// The trade-off: the dashboard's free-text colour field keeps the case it is
// typed in, so an organisation that typed exactly this string in upper case
// renders neutral grey instead of the blue-leaning expansion. The 2026-09-29
// prod census found the upper-case value only on forms that also carry the
// dashboard's own default dark pair, and the lower-case spelling nowhere
// (`implementation/con-331-unbranded-placeholder.md`), so that is the accepted
// cost.
import { generateBrandColors } from './HSB';
import { UNBRANDED_THEME } from './unbrandedTheme';

// cs-allow-hardcoded: a stored-data sentinel compared against, never painted — the pre-PR-1542 dashboard FORM_DEFAULT_COLOR, which legacy forms saved without a colour still carry (CON-331)
export const LEGACY_UNBRANDED_FORM_COLOR = '#64748B';

const PALETTE_STEPS = Object.keys(UNBRANDED_THEME.brandColors);

/**
 * @param {unknown} color A form's stored colour (`lightColor` / `color`).
 * @returns {boolean} true when the value is the legacy "no colour" placeholder.
 */
export const isUnbrandedFormColor = (color) => color === LEGACY_UNBRANDED_FORM_COLOR;

/**
 * The palette a stored colour stands for: the neutral donor set for the legacy
 * placeholder, `generateBrandColors` for everything else.
 *
 * Returns a fresh object each call, so a caller that writes into its palette
 * cannot reach the frozen `UNBRANDED_THEME`.
 *
 * @param {string} baseColor
 * @returns {Record<string, string>}
 */
export const brandPaletteFor = (baseColor) =>
  isUnbrandedFormColor(baseColor)
    ? { ...UNBRANDED_THEME.brandColors }
    : generateBrandColors(baseColor);

/**
 * @param {unknown} palette
 * @returns {boolean} true when every step equals the neutral donor set, i.e.
 *   the palette `brandPaletteFor` returns for an organisation with no colour.
 *   `generateBrandColors` pins its pressed step's saturation, so no input it
 *   expands can produce this set.
 */
export const isUnbrandedPalette = (palette) =>
  Boolean(palette) &&
  typeof palette === 'object' &&
  PALETTE_STEPS.every((step) => palette[step] === UNBRANDED_THEME.brandColors[step]);
